import { chromeStorage } from './storage'

export interface StorageError {
  type: 'GET_ERROR' | 'SET_ERROR' | 'REMOVE_ERROR' | 'UNKNOWN_ERROR'
  message: string
  originalError?: Error
}

/**
 * Chrome Storage Adapter for Zustand Persist Middleware
 * Handles async Chrome Storage API with Zustand
 */
export function createChromeStorageAdapter(_name: string) {
  return {
    getItem: async (key: string): Promise<string | null> => {
      try {
        const data = await chromeStorage.get(key)
        if (data === null || data === undefined) return null
        return typeof data === 'string' ? data : JSON.stringify(data)
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        console.error(`❌ Failed to get ${key} from Chrome Storage:`, err)
        return null
      }
    },
    setItem: async (key: string, value: string): Promise<void> => {
      try {
        await chromeStorage.set(key, value)
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        console.error(`❌ Failed to set ${key} in Chrome Storage:`, err)
        throw {
          type: 'SET_ERROR',
          message: `Failed to persist ${key}`,
          originalError: err,
        } as StorageError
      }
    },
    removeItem: async (key: string): Promise<void> => {
      try {
        await chromeStorage.remove(key)
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        console.error(`❌ Failed to remove ${key} from Chrome Storage:`, err)
      }
    },
  }
}

/**
 * Sync Cache Layer for Chrome Storage
 * Provides immediate reads while async writes happen in background
 */
class CacheStorage {
  private cache: Record<string, any> = {}
  private initialized = false
  private initPromise: Promise<void> | null = null

  async initialize(): Promise<void> {
    if (this.initialized) return
    if (this.initPromise) return this.initPromise

    this.initPromise = this.doInitialize()
    await this.initPromise
  }

  private async doInitialize(): Promise<void> {
    if (this.initialized) return

    try {
      const keys = [
        'settings-theme',
        'settings-language',
        'settings-calendar',
        'bookmarks',
        'habits',
        'weeks',
        'tasks',
        'finance',
      ]

      const results = await Promise.allSettled(
        keys.map(key => chromeStorage.get(key))
      )

      results.forEach((result, index) => {
        const key = keys[index]
        if (result.status === 'fulfilled' && result.value) {
          try {
            this.cache[key] = typeof result.value === 'string' ? JSON.parse(result.value) : result.value
          } catch (e) {
            this.cache[key] = result.value
          }
        }
      })

      this.initialized = true
      console.log('✅ Cache Storage initialized')
    } catch (error) {
      console.error('❌ Failed to initialize cache storage:', error)
      this.initialized = true // Mark as initialized even on error
    }
  }

  get(key: string): any {
    return this.cache[key]
  }

  set(key: string, value: any): void {
    // Immediate local update
    this.cache[key] = value

    // Async persist to Chrome Storage
    const serialized = typeof value === 'string' ? value : JSON.stringify(value)
    chromeStorage.set(key, serialized).catch((error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error))
      console.error(`❌ Failed to persist ${key}:`, err)
    })
  }

  remove(key: string): void {
    delete this.cache[key]
    chromeStorage.remove(key).catch((error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error))
      console.error(`❌ Failed to remove ${key}:`, err)
    })
  }

  isInitialized(): boolean {
    return this.initialized
  }
}

export const syncStorage = new CacheStorage()
